import { useEffect, useLayoutEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";

/**
 * Lightweight spotlight product tour.
 *
 * Props:
 *   steps: [{ selector, title, body, placement?: "top"|"bottom"|"left"|"right"|"center", route?: string }]
 *   open: boolean
 *   onClose: () => void
 *
 * The overlay uses 4 fixed-position dark panels to create a cutout around the
 * target element (avoids SVG mask complexity + works on any background).
 */
const PAD = 8;         // px of padding around target
const TOOLTIP_W = 340; // px

export default function ProductTour({ steps, open, onClose }) {
  const navigate = useNavigate();
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const [ready, setReady] = useState(false);

  const step = steps?.[i];
  const isCenter = step?.placement === "center";

  // Reset index when tour is (re)opened. Do NOT touch `ready` here — the
  // layout effect below owns that state and will fight this reset otherwise.
  useEffect(() => {
    if (open) setI(0);
  }, [open]);

  // Navigate to the target route (if defined) before measuring
  useEffect(() => {
    if (!open || !step) return;
    if (step.route && window.location.pathname !== step.route) {
      navigate(step.route);
    }
  }, [open, step, navigate]);

  // Measure the target element (with retries — component may still be mounting)
  useLayoutEffect(() => {
    if (!open || !step) return;
    setReady(false);
    if (isCenter) { setRect(null); setReady(true); return; }

    let cancelled = false;
    let attempts = 0;
    const measure = () => {
      if (cancelled) return;
      const el = document.querySelector(step.selector);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
        // Give the scroll a beat to settle
        setTimeout(() => {
          if (cancelled) return;
          const r = el.getBoundingClientRect();
          setRect({
            top: r.top,
            left: r.left,
            width: r.width,
            height: r.height,
          });
          setReady(true);
        }, 250);
      } else if (attempts < 30) {
        attempts += 1;
        setTimeout(measure, 100);
      } else {
        // Give up on this target — center it instead
        setRect(null);
        setReady(true);
      }
    };
    measure();

    const onResize = () => {
      const el = document.querySelector(step.selector);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      cancelled = true;
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [open, step, isCenter, i]);

  const next = useCallback(() => {
    if (i < steps.length - 1) setI((v) => v + 1);
    else onClose?.();
  }, [i, steps, onClose]);

  const prev = useCallback(() => {
    if (i > 0) setI((v) => v - 1);
  }, [i]);

  // Keyboard: Esc closes, arrows navigate
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose?.(); }
      else if (e.key === "ArrowRight" || e.key === "Enter") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, next, prev, onClose]);

  if (!open || !step) return null;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const MARGIN = 12;                                     // gap from target
  const EDGE = 12;                                       // gap from viewport edge
  // Responsive tooltip width: full-viewport-minus-padding on phones, capped at TOOLTIP_W
  const width = Math.min(TOOLTIP_W, vw - EDGE * 2);

  // Compute tooltip position with auto-flip + clamping so it never leaves the viewport.
  let tipStyle;
  if (isCenter || !rect) {
    tipStyle = {
      top: `${Math.max(EDGE, vh / 2 - 130)}px`,
      left: `${(vw - width) / 2}px`,
      width: `${width}px`,
    };
  } else {
    let placement = step.placement || (rect.top > vh / 2 ? "top" : "bottom");
    // Assume tooltip is ~200px tall for placement fitting decisions
    const H = 220;
    // Auto-flip when the chosen side doesn't fit
    if (placement === "left"   && rect.left - MARGIN - width < EDGE)  placement = "right";
    if (placement === "right"  && rect.left + rect.width + MARGIN + width > vw - EDGE) placement = "left";
    if (placement === "top"    && rect.top - MARGIN - H < EDGE) placement = "bottom";
    if (placement === "bottom" && rect.top + rect.height + MARGIN + H > vh - EDGE) placement = "top";
    // If still doesn't fit horizontally (small screens with side placement) → fall back to bottom
    if ((placement === "left"  && rect.left - MARGIN - width < EDGE) ||
        (placement === "right" && rect.left + rect.width + MARGIN + width > vw - EDGE)) {
      placement = rect.top + rect.height + MARGIN + H <= vh - EDGE ? "bottom" : "top";
    }

    const clampX = (x) => Math.min(Math.max(x, EDGE), vw - width - EDGE);
    const clampY = (y) => Math.min(Math.max(y, EDGE), vh - H - EDGE);

    if (placement === "top") {
      const top = rect.top - MARGIN;
      const left = clampX(rect.left + rect.width / 2 - width / 2);
      tipStyle = { top: `${top}px`, left: `${left}px`, width: `${width}px`, transform: "translateY(-100%)" };
    } else if (placement === "left") {
      const top = clampY(rect.top + rect.height / 2 - 80);
      const left = rect.left - MARGIN;
      tipStyle = { top: `${top}px`, left: `${left}px`, width: `${width}px`, transform: "translateX(-100%)" };
    } else if (placement === "right") {
      const top = clampY(rect.top + rect.height / 2 - 80);
      const left = clampX(rect.left + rect.width + MARGIN);
      tipStyle = { top: `${top}px`, left: `${left}px`, width: `${width}px` };
    } else {
      // bottom
      const top = rect.top + rect.height + MARGIN;
      const left = clampX(rect.left + rect.width / 2 - width / 2);
      tipStyle = { top: `${top}px`, left: `${left}px`, width: `${width}px` };
    }
  }

  // Cutout via 4 dark rects around the target (only when we have a rect).
  // z-index 9990+ keeps us above Radix Dialog (z-50) and any other portal on mobile.
  const Z_OVERLAY = 9990;
  const Z_RING = 9991;
  const Z_TOOLTIP = 9999;
  const overlay = (() => {
    if (isCenter || !rect) {
      return (
        <div
          className="fixed inset-0 bg-black/75 backdrop-blur-sm"
          style={{ zIndex: Z_OVERLAY, touchAction: "manipulation" }}
          onClick={onClose}
          data-testid="tour-overlay-full"
        />
      );
    }
    const t = Math.max(0, rect.top - PAD);
    const l = Math.max(0, rect.left - PAD);
    const w = rect.width + PAD * 2;
    const h = rect.height + PAD * 2;
    const bg = "bg-black/75 backdrop-blur-sm";
    const panelStyleBase = { touchAction: "manipulation" };
    return (
      <>
        <div className={`fixed left-0 right-0 top-0 ${bg}`} style={{ ...panelStyleBase, height: `${t}px`, zIndex: Z_OVERLAY }} onClick={onClose} />
        <div className={`fixed left-0 ${bg}`} style={{ ...panelStyleBase, top: `${t}px`, height: `${h}px`, width: `${l}px`, zIndex: Z_OVERLAY }} onClick={onClose} />
        <div className={`fixed right-0 ${bg}`} style={{ ...panelStyleBase, top: `${t}px`, height: `${h}px`, left: `${l + w}px`, zIndex: Z_OVERLAY }} onClick={onClose} />
        <div className={`fixed left-0 right-0 ${bg}`} style={{ ...panelStyleBase, top: `${t + h}px`, bottom: 0, zIndex: Z_OVERLAY }} onClick={onClose} />
        {/* Highlight ring */}
        <div
          className="fixed pointer-events-none border-2 border-[#0D9488] shadow-[0_0_0_9999px_rgba(0,0,0,0)]"
          style={{
            top: `${t}px`,
            left: `${l}px`,
            width: `${w}px`,
            height: `${h}px`,
            zIndex: Z_RING,
            boxShadow: "0 0 0 2px rgba(240,185,11,0.4), 0 0 24px rgba(240,185,11,0.35)",
            animation: "tourPulse 2s ease-in-out infinite",
          }}
          data-testid="tour-highlight"
        />
      </>
    );
  })();

  // Portal to document.body so `position: fixed` resolves against the viewport,
  // not against any transformed ancestor (e.g. Radix Dialog uses translate()
  // which turns it into the containing block for fixed descendants).
  return createPortal(
    <>
      <style>{`
        @keyframes tourPulse {
          0%, 100% { box-shadow: 0 0 0 2px rgba(240,185,11,0.4), 0 0 24px rgba(240,185,11,0.35); }
          50%      { box-shadow: 0 0 0 3px rgba(240,185,11,0.7), 0 0 32px rgba(240,185,11,0.55); }
        }
      `}</style>
      {overlay}

      {/* Tooltip — mobile-safe: high z, touch-manipulation, big tap targets */}
      {ready && (
        <div
          className="fixed bg-white border border-[#0D9488]/60 p-5 shadow-2xl"
          style={{
            ...tipStyle,
            zIndex: Z_TOOLTIP,
            pointerEvents: "auto",
            touchAction: "manipulation",
            WebkitTapHighlightColor: "transparent",
          }}
          data-testid="tour-tooltip"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Sparkles className="w-3 h-3 text-[#0D9488]" />
              <span className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-[#0D9488]">
                // Tour · {i + 1} / {steps.length}
              </span>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] -mr-2 -mt-2 text-slate-500 hover:text-slate-900 transition-colors"
              style={{ touchAction: "manipulation" }}
              data-testid="tour-close"
              aria-label="Close tour"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <h3 className="font-display font-black text-xl uppercase tracking-tight text-slate-900 leading-tight mb-2">
            {step.title}
          </h3>
          <p className="text-sm text-slate-600 leading-relaxed">{step.body}</p>
          <p className="text-[10px] font-mono-num uppercase tracking-widest text-slate-400 mt-3" data-testid="tour-dismiss-hint">
            Tip: click outside or press Esc to close
          </p>

          {/* Progress bar */}
          <div className="mt-4 h-1 bg-slate-50 relative overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-[#0D9488] transition-all duration-300"
              style={{ width: `${((i + 1) / steps.length) * 100}%` }}
            />
          </div>

          {/* Controls — 44px min touch targets, explicit touch-action */}
          <div className="mt-4 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center min-h-[44px] px-2 -ml-2 text-xs font-mono-num uppercase tracking-widest text-slate-600 hover:text-slate-900 active:text-[#0D9488] transition-colors"
              style={{ touchAction: "manipulation" }}
              data-testid="tour-skip"
            >
              Skip tour
            </button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={prev}
                disabled={i === 0}
                className="inline-flex items-center gap-1 min-h-[44px] px-4 border border-slate-200 hover:border-[#0D9488]/50 active:bg-[#0D9488]/10 text-slate-800 hover:text-[#0D9488] disabled:opacity-40 disabled:pointer-events-none font-mono-num text-[11px] uppercase tracking-widest transition-colors"
                style={{ touchAction: "manipulation" }}
                data-testid="tour-prev"
              >
                <ChevronLeft className="w-3 h-3" /> Back
              </button>
              <button
                type="button"
                onClick={next}
                className="inline-flex items-center gap-1 min-h-[44px] px-5 bg-[#0D9488] hover:bg-[#0F766E] active:bg-[#B58A0A] text-black font-mono-num text-[11px] uppercase tracking-widest font-black transition-colors"
                style={{ touchAction: "manipulation" }}
                data-testid="tour-next"
              >
                {i === steps.length - 1 ? "Finish" : "Next"} <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
}
